// A dónde volver después de crear la cuenta o iniciar sesión (CC-CONSOLE#1561).
//
// Quien abre `/dispositivos/vincular` sin sesión se manda al registro con
// `?next=%2Fdispositivos%2Fvincular`. Ese `next` se guarda aquí (sessionStorage
// como el prellenado del alta, con respaldo en memoria) para que sobreviva a
// `/verificar`, a Google y al «ya tengo cuenta» → login; al entrar, la
// plataforma lo consume y lleva a la persona a vincular su equipo en vez de
// al selector de licencias o a la bienvenida.
//
// SEGURIDAD: `next` llega en la URL, así que cualquiera puede armar un enlace
// con él. Sólo se acepta una ruta interna de la LISTA BLANCA (`/` inicial, sin
// `//`, sin esquema, sin `\`, sin caracteres de control). Todo lo demás se
// ignora: nada de redirecciones abiertas a otro dominio.
//
// Segundo uso: el correo de bienvenida de un transportista dado de alta por
// Administración de Cargo Market (cargoCarrierInviteService) lleva
// `/reset-password?token=…&next=/cargo-market/transportista/verificacion`; al
// definir la contraseña se entra con ese `next` y se llega a su perfil.
//
// Sin dependencias a propósito (ni React, ni router, ni analítica).

/** Donde se vincula un dispositivo. */
export const DEVICE_LINK_PATH = "/dispositivos/vincular";

/** Ficha y expediente del transportista (onboarding del alta por la plataforma). */
export const CARRIER_ONBOARDING_PATH = "/cargo-market/transportista/verificacion";

/**
 * Bolsa de cargas del transportista (CC-CONSOLE#1639): la landing manda aquí a
 * quien pulsó «Ofertar»/«Contratar» en una carga pública, con `?load=<id>`
 * para que la bolsa la abra al entrar.
 */
export const CARRIER_BOARD_PATH = "/cargo-market/transportista/bolsa";

/** Publicar una carga como embarcador (mismo flujo desde la landing). */
export const SHIPPER_PUBLISH_PATH = "/cargo-market/embarcador/publicar";

/** Rutas a las que se puede volver tras el alta / login. */
export const ALLOWED_NEXT_PATHS = [DEVICE_LINK_PATH, CARRIER_ONBOARDING_PATH, CARRIER_BOARD_PATH, SHIPPER_PUBLISH_PATH] as const;

const NEXT_KEY = "signupNext";
const MAX = 512;

// Respaldo en memoria si no hay sessionStorage (modo privado estricto).
let memoryNext: string | null = null;

const session = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * La ruta interna segura que describe `value`, o null. Conserva la query (p. ej.
 * `?back=/bienvenida`) pero nunca el host: el resultado siempre empieza con
 * una de las rutas de `ALLOWED_NEXT_PATHS`.
 */
/** Id de carga tal como lo publica el API (`?load=` de la bolsa). */
const LOAD_ID_RE = /^[A-Za-z0-9_-]{1,80}$/;

export const sanitizeNextPath = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw || raw.length > MAX) return null;
  // Sólo rutas absolutas del mismo sitio: «/algo», nunca «//host», «/\host»,
  // «https://…», «javascript:…» ni nada con caracteres de control.
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  if (raw.includes("\\")) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, "https://next.invalid");
  } catch {
    return null;
  }
  if (url.origin !== "https://next.invalid") return null;
  const pathname = url.pathname.replace(/\/+$/, "") || "/";
  if (!(ALLOWED_NEXT_PATHS as readonly string[]).includes(pathname)) return null;
  // La bolsa sólo conserva `load=<id>` válido (CC-CONSOLE#1639); lo demás se tira.
  if (pathname === CARRIER_BOARD_PATH) {
    const load = url.searchParams.get("load");
    return load && LOAD_ID_RE.test(load) ? `${pathname}?load=${encodeURIComponent(load)}` : pathname;
  }
  return `${pathname}${url.search}`;
};

const isNextTo = (path: string, next: string | null | undefined): boolean =>
  typeof next === "string" && (next === path || next.startsWith(`${path}?`));

/** ¿`next` lleva a vincular un dispositivo? */
export const isDeviceLinkNext = (next: string | null | undefined): boolean => isNextTo(DEVICE_LINK_PATH, next);

/** ¿`next` lleva al perfil del transportista (alta desde Cargo Market)? */
export const isCarrierOnboardingNext = (next: string | null | undefined): boolean => isNextTo(CARRIER_ONBOARDING_PATH, next);

/** ¿`next` lleva a la bolsa del transportista (una carga de la landing)? */
export const isCarrierBoardNext = (next: string | null | undefined): boolean => isNextTo(CARRIER_BOARD_PATH, next);

/** ¿`next` lleva a publicar una carga como embarcador? */
export const isShipperPublishNext = (next: string | null | undefined): boolean => isNextTo(SHIPPER_PUBLISH_PATH, next);

/** ¿`next` es cualquiera de los de Cargo Market (perfil, bolsa o publicar)? */
export const isCargoMarketNext = (next: string | null | undefined): boolean =>
  isCarrierOnboardingNext(next) || isCarrierBoardNext(next) || isShipperPublishNext(next);

/** El id de carga de un `next` a la bolsa (`?load=<id>`), o null. */
export const loadIdOfNext = (next: string | null | undefined): string | null => {
  if (!isCarrierBoardNext(next)) return null;
  const at = (next as string).indexOf("?");
  const id = at >= 0 ? new URLSearchParams((next as string).slice(at + 1)).get("load") : null;
  return id && LOAD_ID_RE.test(id) ? id : null;
};

/** De qué flujo es un `next` permitido. */
type NextIntent = "vincular" | "cargo-market";
const intentOf = (next: string): NextIntent | null =>
  isDeviceLinkNext(next) ? "vincular" : isCargoMarketNext(next) ? "cargo-market" : null;

/**
 * Vigencia de un `next` guardado: si la persona abandona el alta, un `next`
 * viejo no debe secuestrar el siguiente inicio de sesión en esa pestaña.
 */
export const NEXT_TTL_MS = 30 * 60 * 1000;

/** Lo que se guarda: la ruta, cuándo y de qué flujo viene. */
interface StoredNext {
  path: string;
  ts: number;
  intent: NextIntent;
}

const readStored = (raw: string | null | undefined): StoredNext | null => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredNext> | null;
    if (!value || typeof value !== "object") return null;
    if (typeof value.ts !== "number" || typeof value.path !== "string") return null;
    if (value.intent !== "vincular" && value.intent !== "cargo-market") return null;
    return value as StoredNext;
  } catch {
    // Formato anterior (la ruta en texto) o basura: no vale.
    return null;
  }
};

const isFresh = (entry: StoredNext | null, now = Date.now()): entry is StoredNext =>
  Boolean(entry) && now - (entry as StoredNext).ts >= 0 && now - (entry as StoredNext).ts <= NEXT_TTL_MS;

/**
 * El `next` vigente (sessionStorage → memoria), ya validado, o null. Sólo vale
 * si su ruta corresponde al flujo con el que se guardó (vincular o Cargo
 * Market) y no tiene más de `NEXT_TTL_MS`; uno vencido se borra al leerlo.
 */
export const getNextPath = (): string | null => {
  let raw: string | null | undefined;
  let fromStorage = false;
  try {
    const storage = session();
    // Con sessionStorage, manda él (otra pestaña o un `clear` no heredan la memoria).
    if (storage) {
      raw = storage.getItem(NEXT_KEY);
      fromStorage = true;
    }
  } catch {
    /* almacenamiento bloqueado: queda la memoria */
  }
  if (!fromStorage) raw = memoryNext;
  const entry = readStored(raw);
  if (!isFresh(entry)) {
    if (raw) clearNextPath();
    return null;
  }
  const safe = sanitizeNextPath(entry.path);
  return safe && intentOf(safe) === entry.intent ? safe : null;
};

/** Guarda un `next` (sólo si es seguro). Devuelve lo que quedó guardado. */
export const saveNextPath = (value: unknown, now: number = Date.now()): string | null => {
  const safe = sanitizeNextPath(value);
  const intent = safe ? intentOf(safe) : null;
  if (!safe || !intent) return null;
  const raw = JSON.stringify({ path: safe, ts: now, intent } satisfies StoredNext);
  memoryNext = raw;
  try {
    session()?.setItem(NEXT_KEY, raw);
  } catch {
    /* sin sessionStorage: queda la memoria */
  }
  return safe;
};

/**
 * Lee `?next=` de una query string y, si es seguro, lo guarda (renovando su
 * vigencia). Un `next` inválido NO borra uno válido que ya estaba guardado.
 * Devuelve el vigente.
 */
export const captureNextPath = (search: string): string | null => {
  const fromUrl = new URLSearchParams(search).get("next");
  if (fromUrl !== null) saveNextPath(fromUrl);
  return getNextPath();
};

/** El `next` válido de ESTA URL (no lo guardado), o null. */
export const nextFromSearch = (search: string): string | null => {
  const safe = sanitizeNextPath(new URLSearchParams(search).get("next"));
  return safe && intentOf(safe) ? safe : null;
};

/**
 * Ya se usó, o la persona tomó otro camino (demo, invitación, pago, otro
 * login, cerrar sesión): que no vuelva a redirigir otro inicio de sesión.
 */
export const clearNextPath = (): void => {
  memoryNext = null;
  try {
    session()?.removeItem(NEXT_KEY);
  } catch {
    /* nada que limpiar */
  }
};

/** Lee y borra el `next` vigente. */
export const consumeNextPath = (): string | null => {
  const next = getNextPath();
  clearNextPath();
  return next;
};

/** Parámetros de la vincular que se pasan tal cual al registro (procedencia e idioma). */
export const FORWARDED_PARAMS = [
  "channel",
  "lang",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "ref",
  "ph_did",
  "ph_sid",
] as const;

/**
 * A dónde mandar a quien abrió `/dispositivos/vincular` sin sesión: al alta
 * gratis con el aviso de vincular y `next` de vuelta. La procedencia (`channel`,
 * `utm_*`, `ref`, `ph_did`/`ph_sid`) y el idioma viajan al registro; lo demás
 * de la query original (p. ej. `back`) se queda dentro de `next`.
 */
export const buildDeviceLinkAuthRedirect = (search: string): string => {
  const original = new URLSearchParams(search);
  const forwarded = new URLSearchParams();
  const inner = new URLSearchParams();
  original.forEach((value, key) => {
    if ((FORWARDED_PARAMS as readonly string[]).includes(key)) forwarded.set(key, value);
    else if (key !== "next" && key !== "intent" && key !== "plan") inner.append(key, value);
  });
  const innerQuery = inner.toString();
  const next = sanitizeNextPath(`${DEVICE_LINK_PATH}${innerQuery ? `?${innerQuery}` : ""}`) ?? DEVICE_LINK_PATH;
  const out = new URLSearchParams({ plan: "gratis", intent: "vincular", next });
  forwarded.forEach((value, key) => out.set(key, value));
  return `/register?${out.toString()}`;
};

/** El login (`/`) conservando `next` (y el idioma, si viene). */
export const loginPathWithNext = (next: string | null, lang?: string | null): string => {
  const params = new URLSearchParams();
  if (next) params.set("next", next);
  if (lang === "es" || lang === "en") params.set("lang", lang);
  const query = params.toString();
  return query ? `/?${query}` : "/";
};

/**
 * El registro conservando `next` (desde el login: «Crear cuenta»). Un `next`
 * de la bolsa o de publicar lleva además la intención de Cargo Market y, para
 * la bolsa, el caso de uso transportista (lo mismo que arma la landing).
 */
export const registerPathWithNext = (next: string | null): string => {
  if (!next) return "/register";
  const params = new URLSearchParams({ plan: "gratis" });
  if (isDeviceLinkNext(next)) params.set("intent", "vincular");
  else if (isCarrierBoardNext(next) || isShipperPublishNext(next)) params.set("intent", "cargo-market");
  if (isCarrierBoardNext(next)) params.set("use_case", "transportista");
  params.set("next", next);
  return `/register?${params.toString()}`;
};
